"use client";

import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { Cloud, Feather, Send } from "lucide-react";
import { IdentityNav } from "@/components/wsm/identity-nav";
import { todayLabel } from "@/lib/wsm/date";

function StoolIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 9.5h10c1 0 1.8.8 1.8 1.8v.2c0 .9-.7 1.6-1.6 1.6H6.8c-.9 0-1.6-.7-1.6-1.6v-.2c0-1 .8-1.8 1.8-1.8Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8.2 13.2 6.8 20M15.8 13.2l1.4 6.8M10 13.2 9.6 18M14 13.2l.4 4.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

const CARDS = [
  { key: "express", route: "/express", Icon: Feather, className: "" },
  { key: "listen", route: "/listen", Icon: StoolIcon, className: "bg-[linear-gradient(145deg,rgba(244,211,142,.82),rgba(255,249,229,.42))]" },
  { key: "ask", route: "/ask", Icon: Send, className: "" },
  { key: "solitude", route: "/solitude", Icon: Cloud, className: "" },
] as const;

export default function HomePage() {
  const router = useRouter();
  const { t, i18n } = useTranslation();

  return (
    <div className="wsm-shell" data-el="home">
      <div className="wsm-grain" />
      <div className="wsm-wash">
        <i className="w1" />
        <i className="w2" />
        <i className="w3" />
      </div>

      <main className="mx-auto w-[min(100%,430px)] min-w-0 px-[18px] pb-[18px]">
        <header className="mb-7 pt-1">
          <div className="mb-1 text-[12px] text-muted-foreground">
            {todayLabel(i18n.language)}
          </div>
          <h1 className="wsm-serif text-[32px] font-light leading-[1.12] tracking-tight">
            {t("app.brand")}
          </h1>
          <p className="mt-3 text-[13px] leading-[1.75] text-muted-foreground">
            {t("home.sub")}
          </p>
          <p className="wsm-serif mt-4 max-w-[310px] text-[22px] leading-[1.35] text-foreground">
            {t("home.tagline")}
          </p>
        </header>

        <div className="grid grid-cols-2 gap-3" data-el="identity-cards">
          {CARDS.map(({ key, route, Icon, className }) => (
            <button
              key={key}
              data-el={`identity-card-${key}`}
              onClick={() => router.push(route)}
              className={`wsm-paper flex h-[168px] flex-col items-start justify-between gap-2 p-4 text-left text-[#344036] transition active:scale-[0.97] ${className}`}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/45 text-primary">
                <Icon size={18} />
              </span>
              <span className="wsm-serif text-[18px] text-[#344036]">
                {t(`home.${key}.title`)}
              </span>
              <span className="text-[12px] leading-[1.5] text-[#344036]/70">
                {t(`home.${key}.desc`)}
              </span>
            </button>
          ))}
        </div>
      </main>

      <IdentityNav />
    </div>
  );
}
